import { useCallback, useState } from 'react'
import { useOpen } from '../components/search-result-listing/header-section/searchbar/hooks'
import AnalyticsService from '../services/analytics-service'
import { commonPageEvents } from '../services/analytics-service/events'
import RegionsAutocompleteSuggestionTransport from '../transports/regions-autocomplete-suggestion-transport'
import { SearchbarHistoryItem } from '../redux/searchbar/types'
import { useDateFormatter } from '../hooks/date'
import { getFilterFromHref } from '../utils/request-utils'
import { getSearchDate } from '../utils/miscellaneous-utils'
import { useSearchUrl } from './search'

interface UseControlledSearchbarLogicProps {
    location: { search: string; regionId: number | null }
    arrivalDate: Date | null
    departureDate: Date | null
    adultsCount: number
    childrenCount: number

    onLocationChange: (loc: { search: string; regionId: number | null }) => void
    onArrivalDateChange: (date: Date | null) => void
    onDepartureDateChange: (date: Date | null) => void
    onAdultsCountChange: (count: number) => void
    onChildrenCountChange: (count: number) => void
}

export function useControlledSearchbarLogic({
    location,
    arrivalDate,
    departureDate,
    adultsCount,
    childrenCount,
    onLocationChange,
    onArrivalDateChange,
    onDepartureDateChange,
    onAdultsCountChange,
    onChildrenCountChange,
}: UseControlledSearchbarLogicProps) {
    const {
        isOpen: isLocationPickerOpen,
        onOpen: onLocationPickerOpen,
        onClose: onLocationPickerClose,
    } = useOpen(false)

    const { isOpen: isDatePickerOpen, onOpen: onDatePickerOpen, onClose: onDatePickerClose } = useOpen(false)

    const { isOpen: isGuestPickerOpen, onOpen: onGuestPickerOpen, onClose: onGuestPickerClose } = useOpen(false)

    const [locationPickerError, setLocationPickerError] = useState('')
    const { format } = useDateFormatter()
    const { getSearchUrl } = useSearchUrl()

    // -------------------------
    // Location Handlers
    // -------------------------

    const handleLocationPickerOpen = useCallback(() => {
        setLocationPickerError('')
        onLocationPickerOpen()
    }, [onLocationPickerOpen])

    const handleLocationInputChange = useCallback(
        (input: string) => {
            onLocationChange(location.search !== input ? { regionId: null, search: input } : location)
        },
        [location, onLocationChange],
    )

    const handleLocationInputSelect = useCallback(
        (value: string) => {
            handleLocationInputChange(value)
            onLocationPickerClose()
            if (!arrivalDate || !departureDate) {
                onDatePickerOpen()
            }
        },
        [handleLocationInputChange, arrivalDate, departureDate, onLocationPickerClose, onDatePickerOpen],
    )

    const handleLocationItemSelect = useCallback(
        (item: RegionsAutocompleteSuggestionTransport) => {
            onLocationChange({ search: item.title, regionId: item.regionId })
            if (!arrivalDate || !departureDate) {
                onDatePickerOpen()
            }
        },
        [arrivalDate, departureDate, onLocationChange, onDatePickerOpen],
    )

    // -------------------------
    // History Select
    // -------------------------

    const handleHistoryItemSelect = useCallback(
        async (history: SearchbarHistoryItem) => {
            const { search, arrival, departure, adults, children } = history
            onLocationChange({ regionId: null, search })
            onArrivalDateChange(arrival)
            onDepartureDateChange(departure)
            onAdultsCountChange(adults)
            onChildrenCountChange(children)

            const searchUrl = await getSearchUrl(getFilterFromHref(window.location.href), {
                regionId: location.regionId,
                search,
                arrival: getSearchDate(arrival),
                departure: getSearchDate(departure),
                adults,
                children,
            })

            window.location.href = `${window.location.origin}${searchUrl}`
        },
        [
            onLocationChange,
            onArrivalDateChange,
            onDepartureDateChange,
            onAdultsCountChange,
            onChildrenCountChange,
            location.regionId,
            getSearchUrl,
        ],
    )

    // -------------------------
    // Date Tracking
    // -------------------------

    const trackArrivalDateChange = useCallback(
        (startDate: Date | null, endDate?: Date | null) => {
            if (!startDate) {
                return
            }

            // Defer analytics to avoid blocking date selection
            setTimeout(() => {
                const formattedStartDateDot = format(startDate, 'dd.MM.yyyy')
                const formattedStartDateDash = format(startDate, 'yyyy-MM-dd')

                AnalyticsService.trackEvent({
                    event: 'datepicker_select',
                    eventScope: 'dates[form_date_from]',
                    eventValue: formattedStartDateDot,
                })

                AnalyticsService.trackEvent({
                    action: 'searchbox_add_arrival',
                    category: 'searchbox',
                    label: formattedStartDateDash,
                })

                if (endDate) {
                    AnalyticsService.trackEvent({
                        eventAction: 'entered_arrival_and_departure',
                        eventLabel: 'tbd',
                    })
                }
            }, 0)
        },
        [format],
    )

    const trackDepartureDateChange = useCallback(
        (startDate: Date | null, endDate: Date | null) => {
            if (!endDate) {
                return
            }

            // Defer analytics to avoid blocking date selection
            setTimeout(() => {
                const formattedEndDateDot = format(endDate, 'dd.MM.yyyy')
                const formattedEndDateDash = format(endDate, 'yyyy-MM-dd')

                AnalyticsService.trackEvent({
                    event: 'datepicker_select',
                    eventScope: 'dates[form_date_to]',
                    eventValue: formattedEndDateDot,
                })

                AnalyticsService.trackEvent({
                    action: 'searchbox_add_departure',
                    category: 'searchbox',
                    label: formattedEndDateDash,
                })

                if (startDate) {
                    AnalyticsService.trackEvent({
                        eventAction: 'entered_arrival_and_departure',
                        eventLabel: 'tbd',
                    })
                }
            }, 0)
        },
        [format],
    )

    const handleDepartureDateChange = useCallback(
        (date: Date | null) => {
            onDepartureDateChange(date)
            trackDepartureDateChange(arrivalDate, date)

            if (date) {
                onDatePickerClose()
            }
        },
        [arrivalDate, onDepartureDateChange, trackDepartureDateChange, onDatePickerClose],
    )

    // -------------------------
    // Guest Picker
    // -------------------------

    const handleGuestPickerOpen = useCallback(() => {
        if (!isGuestPickerOpen) {
            AnalyticsService.trackEvent({
                event: commonPageEvents.searchBarSection.CLICK_PERS_FILTER,
                eventLabel: 'searchbar',
            })
        }
        onGuestPickerOpen()
    }, [isGuestPickerOpen, onGuestPickerOpen])

    const handleGuestPickerClose = useCallback(() => {
        if (isGuestPickerOpen) {
            AnalyticsService.trackEvent({
                event: commonPageEvents.searchBarSection.SENT_PERS_FILTER,
                eventLabel: 'searchbar',
                eventValue: `adults=${adultsCount} kids=${childrenCount}`,
            })
        }
        onGuestPickerClose()
    }, [isGuestPickerOpen, adultsCount, childrenCount, onGuestPickerClose])

    return {
        isLocationPickerOpen,
        isDatePickerOpen,
        isGuestPickerOpen,
        onLocationPickerClose,
        onDatePickerClose,
        onGuestPickerClose,
        onLocationPickerOpen,
        onDatePickerOpen,
        onGuestPickerOpen,

        locationPickerError,
        setLocationPickerError,

        handleLocationPickerOpen,
        handleLocationInputChange,
        handleLocationInputSelect,
        handleLocationItemSelect,
        handleHistoryItemSelect,

        handleGuestPickerOpen,
        handleGuestPickerClose,

        trackArrivalDateChange,
        trackDepartureDateChange,
        handleDepartureDateChange,
    }
}
